import { FormCol4 } from 'motion-components';
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaBalanceScale, FaBalanceScaleLeft } from 'react-icons/fa';

/** Map org field key to request (tab "Αίτημα") field key for comparison */
const ORG_TO_REQUEST_FIELD: Record<string, string> = {
  orgChemistry: 'chemistry',
  orgBatteryWeight: 'batteryWeight',
  orgPieceCount: 'pieceCount',
  orgBatteryHeight: 'batteryHeight',
  orgBatteryLength: 'batteryLength',
  orgBatteryWidth: 'batteryWidth',
  batteryCondition: 'technicianAssessment',
};

/** Numeric org fields: compare as numbers */
const NUMERIC_ORG_FIELDS = new Set([
  'orgBatteryWeight',
  'orgPieceCount',
  'orgBatteryHeight',
  'orgBatteryLength',
  'orgBatteryWidth',
]);

function valuesDiffer(orgField: string, currentValue: unknown, requestValue: unknown): boolean {
  const isNumeric = NUMERIC_ORG_FIELDS.has(orgField);

  if (isNumeric) {
    const a = currentValue === '' || currentValue == null ? NaN : Number(currentValue);
    const b = requestValue === '' || requestValue == null ? NaN : Number(requestValue);
    if (Number.isNaN(a) && Number.isNaN(b)) return false;
    if (Number.isNaN(a) || Number.isNaN(b)) return true;
    return a !== b;
  }

  const a = currentValue === null || currentValue === undefined ? '' : String(currentValue).trim();
  const b = requestValue === null || requestValue === undefined ? '' : String(requestValue).trim();
  return a.toLowerCase() !== b.toLowerCase();
}

export type DiffValueDecorationProps = {
  /** xrbData from form values (values.xrbData) */
  xrbData?: Record<string, unknown> | null;
  /** One of: orgChemistry, orgBatteryWeight, orgPieceCount, orgBatteryHeight, orgBatteryLength, orgBatteryWidth, batteryCondition */
  orgField:
    | 'orgChemistry'
    | 'orgBatteryWeight'
    | 'orgPieceCount'
    | 'orgBatteryHeight'
    | 'orgBatteryLength'
    | 'orgBatteryWidth'
    | 'batteryCondition';
  className?: string;
  children?: React.ReactNode;
  as?: React.ComponentType<any>;
};

/**
 * Decoration for "Οργάνωση" fields: shows an icon to the right of the input
 * when the value differs from the corresponding "Αίτημα" (request) tab value.
 * Style follows AiExtractionDecoration (icon right of field, tooltip).
 */
export const DiffValueDecoration: React.FC<DiffValueDecorationProps> = ({
  xrbData,
  orgField,
  className = '',
  children,
  as = FormCol4,
}) => {
  const Wrapper = as;
  const requestField = ORG_TO_REQUEST_FIELD[orgField];
  const currentValue = xrbData?.[orgField];
  const requestValue = requestField ? xrbData?.[requestField] : undefined;

  const differs = valuesDiffer(orgField, currentValue, requestValue);

  return (
    <Wrapper>
      {children && (
        <div
          className={`position-relative overflow-visible ${className}`.trim()}
          style={differs ? { paddingRight: '1.75rem' } : undefined}
        >
          {children}
          {differs && (
            <span
              className="position-absolute bottom-0 mb-1 text-error"
              style={{ right: '0.5rem', lineHeight: 1 }}
            >
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`diff-${orgField}`}>
                    Διαφέρει από το αίτημα
                    {requestValue != null && String(requestValue).trim() !== '' && (
                      <>
                        <br />
                        <span className="small">Αίτημα: {String(requestValue)}</span>
                      </>
                    )}
                  </Tooltip>
                }
              >
                <span style={{ cursor: 'help' }} role="img" aria-label="Διαφέρει από το αίτημα">
                  <FaBalanceScaleLeft size={16} />
                </span>
              </OverlayTrigger>
            </span>
          )}
        </div>
      )}
    </Wrapper>
  );
};

export default DiffValueDecoration;
